<template>
    <section class="test-drive">
        <div class="select-model-extras__container container">
            <StepBar :step="2" step-name="Select a model" />
            <Navigation :go-to-step="{ name: 'select-model', params: { brand: this.brand, }, }" />

            <div class="test-drive__content">
                <loading-spinner v-if="loading"></loading-spinner>
                <form @submit.prevent="submit" ref="form" class="select-model-extras__content--form" v-show="!loading">
                    <h2 class="title capitalise">{{ model.model_title }} Test Drive</h2>

                    <p class="description">
                        We are thrilled you want to take a <span class="capitalise">{{ model.model_title }}</span> test drive. To make your experience the best it can be, please complete the questions below:
                    </p>

                    <!-- Only to be displayed on mobile -->
                    <div class="select-model-extras__content--info-mobile">
                        <img class="img-responsive select-model-extras__content--image-mobile" :src="model.image" :alt="model.model_title" />
                        <div class="select-model-extras__content--details">
                            <h3 class="select-model-extras__h3 capitalise">{{ model.model_title }}</h3>
                        </div>
                        <div class="select-model-extras__content--fix-spacing">
                            <Navigation :go-to-step="{ name: 'select-model', params: { brand: this.brand, }, }" link-text="Change model" />
                        </div>
                    </div>
                    <!-- Only to be displayed on mobile -->

                    <div class="card">
                        <div class="card__inner">
                            <p v-if="preselectedFuel" class="select-model-extras__content--question">
                                <span class="capitalise">{{ model.model_title }}</span> is exclusively available with {{ completeFuelChoiceName }} engine. Therefore we have preselected this choice for you.
                            </p>
                            <p v-else class="select-model-extras__content--question">Please select your preferred engine</p>
                            <div class="select-model-extras__content--options">
                                <div class="select-model-extras__content--option" v-for="(type, index) in fuelTypes" :key="index">
                                    <input
                                        class="select-model-extras__content--radio"
                                        type="radio"
                                        name="fuelType"
                                        v-model="fuelChoice"
                                        :id="type"
                                        :value="type"
                                        data-validate="radio"
                                        data-validate-message="Please choose a fuel type"
                                        data-error-target="test-drive-fuel-type-errors"
                                    />
                                    <label :for="type">{{ type }}</label>
                                </div>
                            </div>
                            <div id="test-drive-fuel-type-errors"></div>

                            <p v-if="preselectedTransmission" class="select-model-extras__content--question">
                                We have preselected {{ transmissionChoice.toLowerCase() }} as your choice of transmission as this is the only option on the <span class="capitalise">{{ model.model_title }}</span
                                >.
                            </p>
                            <p v-else class="select-model-extras__content--question">Please select your preferred transmission</p>
                            <div class="select-model-extras__content--options">
                                <div class="select-model-extras__content--option" v-for="(transmission, index) in transmissions" :key="index">
                                    <input
                                        class="select-model-extras__content--radio"
                                        type="radio"
                                        name="transmission"
                                        v-model="transmissionChoice"
                                        :id="transmission.toLowerCase()"
                                        :value="transmission"
                                        data-validate="radio"
                                        data-validate-message="Please choose a transmission type"
                                        data-error-target="test-drive-transmission-type-errors"
                                    />
                                    <label :for="transmission.toLowerCase()">{{ transmission.toLowerCase() }}</label>
                                </div>
                            </div>
                            <div id="test-drive-transmission-type-errors"></div>

                            <p class="select-model-extras__content--question">
                                Please let us know if you have any specific requests for your <span class="capitalise">{{ model.model_title }}</span> test drive.
                            </p>
                            <textarea class="select-model-extras__textarea" name="notes" v-model="notes"></textarea>
                        </div>
                    </div>

                    <button type="submit" class="btn btn--primary select-model-extras__content--continue" :class="{ disabled: !fuelChoice || !transmissionChoice }">Continue</button>
                </form>

                <!-- Only to be displayed on bigger screens -->
                <div class="select-model-extras__content--info-desktop">
                    <picture v-if="model.image">
                        <source v-if="model.image_webp" :srcset="model.image_webp" type="image/webp" />
                        <img class="img-responsive select-model__image" :src="model.image" :alt="model.model_title" />
                    </picture>

                    <div class="select-model-extras__content--details">
                        <h3 class="select-model-extras__h3 capitalise">{{ model.model_title }}</h3>
                    </div>

                    <div class="select-model-extras__content--fix-spacing">
                        <Navigation :go-to-step="{ name: 'select-model', params: { brand: this.brand, }, }" link-text="Change model" />
                    </div>
                </div>
                <!-- Only to be displayed on bigger screens -->
            </div>
        </div>
    </section>
</template>

<script>
import StepBar from "../components/StepBar";
import LoadingSpinner from "../components/LoadingSpinner";
import Navigation from "../components/Navigation";
import { mapState } from "vuex";
import EventObject from "../../modules/tracking/EventObject";

export default {
    props: {
        brand: {
            required: true,
        },
        urlModel: {
            required: true,
        },
    },
    components: {
        LoadingSpinner,
        StepBar,
        Navigation,
    },
    data() {
        return {
            loading: true,
            fuelTypes: [],
            transmissions: [],
            validator: false,
            preselectedFuel: false,
            preselectedTransmission: false,
        };
    },
    methods: {
        async submit() {
            if (!await this.validator.isValid()) {
                return;
            }
            this.$router.push({
                name: "select-dealer",
                params: {
                    brand: this.brand,
                    urlModel: this.urlModel,
                },
            });
        },

        isVowel(letter) {
            return /[aeiouAEIOU]/.test(letter);
        },
    },
    created() {
        axios.get(`/api/test-drive/model-extra/${this.urlModel}`, {
                params: {
                    brand: this.brand,
                }
            })
            .then(response => {
                this.$store.commit("setModel", response.data.newModel);
                this.fuelTypes = response.data.fuelTypes;
                let electricOrHybridPossible = false;
                this.fuelTypes.forEach((fuelType) => {
                    const lowercaseFuelChoice = fuelType.toLowerCase();
                    if (lowercaseFuelChoice.indexOf("electric") >= 0 || lowercaseFuelChoice.indexOf("hybrid") >= 0) {
                        electricOrHybridPossible = true;
                    }
                });
                this.$store.commit("setElectricOrHybridSelected", electricOrHybridPossible);

                this.transmissions = response.data.transmissions;

                this.fuelTypes.length === 1 ? ((this.fuelChoice = this.fuelTypes[0]), (this.preselectedFuel = true)) : "";
                this.transmissions.length === 1 ? ((this.transmissionChoice = this.transmissions[0]), (this.preselectedTransmission = true)) : "";
                this.loading = false;

                const titleEl = document.querySelector('title');
                if (titleEl) {
                    titleEl.innerHTML = `${this.model.model_title} Test Drive | ${this.model.model_title}`;
                }
                const metaDescription = document.querySelector('meta[name="description"]');
                if (metaDescription) {
                    metaDescription.setAttribute('content', `Test drive the latest ${this.model.model_title} at your local Stellantis &You UK dealership at a time which suits you.`);
                }

                if (this.step2aPage === true) {
                    new EventObject("New Car - Test Drive Form", "Revisit", "Step 2a Page").dispatch();
                } else {
                    new EventObject("New Car - Test Drive Form", "Visited", "Step 2a Page").dispatch();
                    this.$store.commit("setStep2aPage", true);
                }

                if (this.validator) {
                    this.$nextTick(() => {
                        this.validator.updateValidatableElements();
                    });
                }
            })
            .catch((error) => {
                console.error(error);
                this.$router.push({
                    name: "select-model",
                    params: {
                        brand: this.brand,
                    },
                });
            });
    },
    mounted() {
        this.validator = new ContactFormValidator({
            element: $(this.$refs.form),
            vueForm: true,
        });
    },
    computed: {
        ...mapState(["step2aPage", 'model']),

        fuelChoice: {
            get() {
                return this.$store.state.fuelChoice;
            },
            set(value) {
                this.$store.commit("setFuelChoice", { fuelChoice: value });
            },
        },
        transmissionChoice: {
            get() {
                return this.$store.state.transmissionChoice;
            },
            set(value) {
                this.$store.commit("setTransmissionChoice", { transmissionChoice: value });
            },
        },
        notes: {
            get() {
                return this.$store.state.notes;
            },
            set(value) {
                this.$store.commit("setNotes", { notes: value });
            },
        },
        completeFuelChoiceName() {
            let complete;

            this.isVowel(this.fuelChoice.charAt(0)) ? (complete = `an ${this.fuelChoice.toLowerCase()}`) : (complete = `a ${this.fuelChoice.toLowerCase()}`);

            return complete;
        },
    },
};
</script>

<style>
</style>


// WEBPACK FOOTER //
// resources/assets/js/test-drive/pages/step-2a.vue